import LanguageSwitcher from '@/components/common/LanguageSwitcher/LanguageSwitcher'
import ListItem from '@/components/common/ListItem'
import {
  ArrowRightIcon,
  AvatarPlaceholderIcon,
  HalfUserIcon,
} from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import useMeasureOptions from '@/features/auth/hooks/useMeasureOptions'
import useTimezoneOptions from '@/features/auth/hooks/useTimezoneOptions'
import useUpdateUserProfile from '@/features/auth/hooks/useUpdateUser'
import useUser from '@/features/auth/hooks/useUser'
import { useTranslation } from '@/hooks/useTranslation'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { changeTheme } from '@/store/slices/themeSlice'
import { mode_type } from '@/theme/types/constants'
import { User } from '@/types/user'
import notificationApi from '@/api/admin/notificationApi'
import {
  Avatar,
  Box,
  Button,
  Dialog,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Typography,
} from '@mui/material'
import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import RemidIcon from '../icons/RemidIcon'
import ChangePasswordView from './ChangePasswordView'
import classes from './styles.module.scss'

import NotificationAlertsModal from '@/features/operations/components/pages/Dashboard/NotificationAlertsModal/NotificationAlertsModal'
import ListSelectAlerts from '@/features/operations/components/pages/Dashboard/NotificationAlertsModal/ListSelectAlerts'

interface UserNavProps {
  mode?: 'dark' | 'light'
  adminPage?: boolean
  dashboardPage?: boolean
  quickView?: boolean
  sx?: any
}

const childMenuStyle = {
  backgroundColor: 'background.default',
  width: '166px',
  // border: '1px solid rgba(132, 132, 132, 0.25)',
  borderRadius: '5px',
  padding: '0px',
  boxShadow: '0px 0px 20px -3px rgba(132, 132, 132, 0.25)',
  '& .MuiMenuItem-root:not(:last-child)': {
    borderBottom: '1px solid #D7D7D7',
  },
  '& .MuiMenuItem-root:hover p, & .MuiMenuItem-root.Mui-selected p': {
    color: 'primary.main',
    fontWeight: 600,
  },
  '& .MuiMenuItem-root:hover, & .MuiMenuItem-root.Mui-selected': {
    backgroundColor: 'background.overrideDefault',
  },
}

const UserNav = ({
  mode = 'light',
  adminPage = false,
  dashboardPage = false,
  quickView = false,
  sx,
}: UserNavProps) => {
  const { logout } = useAuthContext()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const themeMode = useSelector(selectTheme)
  const dispatch = useDispatch()
  const { data: user } = useUser<User>()

  const DATA_MODE_THEME = [
    {
      name: t('userNavThemesLight'),
      mode: 'light',
    },
    {
      name: t('userNavThemesDark'),
      mode: 'dark',
    },
  ]
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false)
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
  const [anchorElGMT, setAnchorElGMT] = useState<null | HTMLElement>(null)
  const [anchorElMetric, setAnchorElMetric] = useState<null | HTMLElement>(null)
  const [anchorElTheme, setAnchorElTheme] = useState<null | HTMLElement>(null)
  const [anchorElAlert, setAnchorElAlert] = useState<null | HTMLElement>(null)
  const [anchorElLanguage, setAnchorElLanguage] = useState<null | HTMLElement>(
    null,
  )

  const [showMetric, setShowMetric] = useState(false)
  const [showTheme, setShowTheme] = useState(false)
  const [showGMT, setShowGMT] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [showLanguage, setShowLanguage] = useState(false)

  const openUser = Boolean(anchorElUser)
  const gmtRef = useRef<any>(null)
  const updateProfile = useUpdateUserProfile()

  const { data: measureOptions } = useMeasureOptions()
  const { data: timezoneOptions } = useTimezoneOptions()

  const handleOpenChangePassword = () => {
    setOpenChangePasswordModal(true)
    handleCloseUser()
  }

  const handleCloseChangePassord = () => {
    setOpenChangePasswordModal(false)
  }

  const handleClickUser = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUser = () => {
    setAnchorElUser(null)
    setAnchorElGMT(null)
    setAnchorElMetric(null)
    setAnchorElAlert(null)
    setAnchorElTheme(null)
    setAnchorElLanguage(null)
    setShowMetric(false)
    setShowGMT(false)
    setShowAlert(false)
    setShowTheme(false)
    setShowLanguage(false)
  }

  const handleOpenGMT = (event: React.MouseEvent<HTMLElement>) => {
    setShowGMT(true)
    setAnchorElGMT(event.currentTarget)
    setTimeout(() => {
      gmtRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 300)
  }

  const handleOpenMetric = (event: React.MouseEvent<HTMLElement>) => {
    setShowMetric(true)
    setAnchorElMetric(event.currentTarget)
  }

  const handleOpenTheme = (event: React.MouseEvent<HTMLElement>) => {
    setShowTheme(true)
    setAnchorElTheme(event.currentTarget)
  }

  const handleOpenAlert = (event: React.MouseEvent<HTMLElement>) => {
    setShowAlert(true)
    setAnchorElAlert(event.currentTarget)
  }

  const handleOpenLanguage = (event: React.MouseEvent<HTMLElement>) => {
    setShowLanguage(true)
    setAnchorElLanguage(event.currentTarget)
  }

  const handleClickMetric = async (id: number, name: string) => {
    if (user?.email) {
      try {
        await notificationApi.updateSubscriptionInfo({
          email: user.email,
          measure: name,
        })
      } catch (error) {
        console.error('Failed to update notification subscription info:', error)
      }
    }

    localStorage.setItem('measure', name)
    updateProfile({ measureId: id })
    setShowMetric(false)
  }

  const handleClickGMT = (id: number) => {
    updateProfile({ gmtTimeZoneId: id })
    setShowGMT(false)
  }

  const avatarRender = () => {
    if (user?.avatar) {
      return (
        <Avatar
          src={user?.avatar}
          sx={{
            backgroundColor: '#C4C4C4',
            height: '34px',
            width: '34px',
            [`& .MuiSvgIcon-root`]: {
              display: 'none',
            },
          }}
        />
      )
    }
    if (!user?.avatar && dashboardPage === false) {
      return (
        <AvatarPlaceholderIcon
          sx={{
            height: '34px',
            width: '34px',
            color: themeMode === mode_type.dark ? '#FFF ' : '#004744',
          }}
        />
      )
    }

    if (!user?.avatar && dashboardPage) {
      return <HalfUserIcon />
    }
  }
  const titleRender = () => {
    let result = ''
    if (user?.organization?.name) result += `${user?.organization?.name}, `
    if (user?.role) result += user?.role
    return result
  }

  const handleLogout = () => {
    logout()
    // localStorage.clear()
  }

  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null)
  const openNotification = Boolean(notificationAnchorEl)

  const handleOpenNotification = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setNotificationAnchorEl(event.currentTarget)
  }

  const handleCloseNotification = () => {
    setNotificationAnchorEl(null)
  }

  const handleThemeChange = (theme: string) => {
    localStorage.setItem('theme', theme)
    dispatch(changeTheme(theme))
  }

  return (
    <form>
      <Grid>
        <Stack direction="row">
          {!adminPage && (
            <Stack justifyContent="center">
              <Button
                className={classes.btnNotification}
                onClick={handleOpenNotification}
                sx={{
                  background:
                    mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.10)'
                      : 'rgba(4, 71, 68, 0.20)',
                  '&:hover': {
                    background:
                      mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.10)'
                        : 'rgba(4, 71, 68, 0.20)',
                  },
                }}
              >
                <RemidIcon sx={{ color: quickView ? '#fff' : 'text.main' }} />
              </Button>
              <Menu
                className={classes.menuNotificationWrapper}
                anchorEl={notificationAnchorEl}
                onClose={handleCloseNotification}
                open={openNotification}
                anchorOrigin={{
                  vertical: dashboardPage ? 'bottom' : 'bottom',
                  horizontal: dashboardPage ? 'right' : 'right',
                }}
                transformOrigin={{
                  vertical: dashboardPage ? 'center' : 'top',
                  horizontal: dashboardPage ? 'left' : 'right',
                }}
              >
                <NotificationAlertsModal />
              </Menu>
            </Stack>
          )}

          <Button
            id="basic-button"
            disableRipple
            disableFocusRipple
            disableElevation
            aria-controls={openUser ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={openUser ? 'true' : undefined}
            onClick={handleClickUser}
            startIcon={avatarRender()}
            sx={{
              textAlign: 'left',
              textTransform: 'capitalize',
              color: 'black',
              mr: '15px',
              maxHeight: '45px',
              background: 'none',
              '&:hover': {
                background: 'none',
              },
              ...sx,
            }}
          >
            <Stack>
              <Typography
                color="text.main"
                sx={{
                  fontSize: '12px',
                  fontWeight: '500',
                  textAlign: 'center',
                  ...(mode === 'dark' && {
                    color: '#fff',
                    fontSize: 16,
                  }),
                }}
              >
                {user?.name}
              </Typography>
              <Typography
                color="text.main"
                sx={{
                  fontSize: '12px',
                  fontWeight: '500',
                  textOverflow: 'ellipsis',
                  width: '100%',
                  overflow: 'hidden',
                  ...(mode === 'dark' && {
                    color: '#fff',
                  }),
                }}
              >
                {/* {titleRender()} */}
              </Typography>
            </Stack>
          </Button>
        </Stack>

        {openUser && (
          <Stack direction="row">
            <Menu
              id="basic-menu"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: dashboardPage ? 'top' : 'bottom',
                horizontal: 'right',
              }}
              open={openUser}
              onClose={handleCloseUser}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
              sx={{
                mt: '4px',
                ...(dashboardPage && {
                  ml: '7vh',
                }),
                [`& .MuiMenu-list`]: {
                  maxWidth: '200px',
                  boxShadow: ' 0px 0px 30px rgba(132, 132, 132, 0.25)',
                  minWidth: '190px',
                  padding: '0px',
                },
                '& .MuiMenuItem-root:hover p': {
                  color: 'text.main',
                },
              }}
            >
              <MenuItem
                sx={{
                  borderBottom: '1px solid #D7D7D7',
                }}
              >
                <Stack>
                  <Typography
                    title={user?.name}
                    color="text.main"
                    sx={{
                      fontSize: '14px',
                      fontWeight: '500',
                      textAlign: 'left',
                      lineHeight: '1.3',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    {user?.name}
                  </Typography>
                  <Typography
                    color="text.main"
                    title={user?.email}
                    sx={{
                      fontSize: '14px',
                      fontWeight: '500',
                      textAlign: 'left',
                      lineHeight: '1.3',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    {user?.email}
                  </Typography>
                </Stack>
              </MenuItem>

              <MenuItem
                onClick={handleOpenAlert}
                sx={{
                  borderBottom: '1px solid #D7D7D7',
                }}
              >
                <ListItemText
                  sx={{
                    [`& .MuiTypography-root`]: {
                      fontSize: '14px',
                      color: 'text.main',
                      fontWeight: '400',
                      lineHeight: '100%',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    color="grey.600"
                    sx={{ padding: '10px 2px' }}
                  >
                    <Typography>{t('alertNotification')}</Typography>
                  </Stack>
                </ListItemText>
              </MenuItem>

              <MenuItem
                onClick={handleOpenTheme}
                sx={{
                  borderBottom: '1px solid #D7D7D7',
                }}
              >
                <ListItemText
                  sx={{
                    [`& .MuiTypography-root`]: {
                      fontSize: '14px',
                      color: 'text.main',
                      fontWeight: '400',
                      lineHeight: '100%',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    color="grey.600"
                    sx={{ padding: '10px 2px' }}
                  >
                    <Typography>{t('userNavTheme')}</Typography>
                    <ArrowRightIcon />
                  </Stack>
                </ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => handleOpenChangePassword()}
                sx={{
                  borderBottom: '1px solid #D7D7D7',
                }}
              >
                <ListItem name={t('changePassword')} />
              </MenuItem>

              <MenuItem
                onClick={handleOpenLanguage}
                sx={{
                  borderBottom: '1px solid #D7D7D7',
                }}
              >
                <ListItemText
                  sx={{
                    [`& .MuiTypography-root`]: {
                      fontSize: '14px',
                      color: 'text.main',
                      fontWeight: '400',
                      lineHeight: '100%',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    color="grey.600"
                    sx={{ padding: '10px 2px' }}
                  >
                    <Typography>{t('userNavLanguage')}</Typography>
                    <ArrowRightIcon />
                  </Stack>
                </ListItemText>
              </MenuItem>

              <MenuItem
                onClick={handleOpenMetric}
                sx={{
                  borderBottom: '1px solid #D7D7D7',
                }}
              >
                <ListItemText
                  sx={{
                    [`& .MuiTypography-root`]: {
                      fontSize: '14px',
                      color: 'text.main',
                      fontWeight: '400',
                      lineHeight: '100%',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    color="grey.600"
                    sx={{ padding: '10px 2px' }}
                  >
                    <Typography>
                      {user?.measure?.name || t('userNavUnitOfMeasurement')}
                    </Typography>
                    <ArrowRightIcon />
                  </Stack>
                </ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => { handleCloseUser(); navigate('/dashboard/changelog') }}
                sx={{ borderBottom: '1px solid #D7D7D7' }}
              >
                <ListItemText>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: 'text.main',
                      fontWeight: '400',
                      lineHeight: '100%',
                      padding: '10px 2px',
                    }}
                  >
                    {t('ChangeLog')}
                  </Typography>
                </ListItemText>
              </MenuItem>

              <MenuItem onClick={() => handleLogout()}>
                <ListItem name={t('userNavLogout')} />
              </MenuItem>
            </Menu>
          </Stack>
        )}

        {showMetric && (
          <Menu
            open={showMetric}
            anchorEl={anchorElMetric}
            onClose={handleCloseUser}
            anchorOrigin={{
              vertical: dashboardPage ? 'bottom' : 'top',
              horizontal: dashboardPage ? 'right' : 'left',
            }}
            transformOrigin={{
              vertical: dashboardPage ? 'center' : 'top',
              horizontal: dashboardPage ? 'left' : 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: 'transparent',
                boxShadow: 'unset !important',
                padding: '0px',
              },
              '& .MuiList-root': {
                padding: '0px !important',
              },
            }}
          >
            <MenuList
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'unset !important',
                padding: '0px',
              }}
            >
              <Box sx={childMenuStyle}>
                {measureOptions?.map((item) => (
                  <MenuItem
                    onClick={() => handleClickMetric(item.id, item.name)}
                    key={item.id}
                    selected={item.id === user?.measure?.id}
                  >
                    <ListItem name={item.name} />
                  </MenuItem>
                ))}
              </Box>
            </MenuList>
          </Menu>
        )}

        {showTheme && (
          <Menu
            open={showTheme}
            anchorEl={anchorElTheme}
            onClose={handleCloseUser}
            anchorOrigin={{
              vertical: dashboardPage ? 'bottom' : 'top',
              horizontal: dashboardPage ? 'right' : 'left',
            }}
            transformOrigin={{
              vertical: dashboardPage ? 'center' : 'top',
              horizontal: dashboardPage ? 'left' : 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                boxShadow: 'unset !important',
                padding: '0px !important',
              },
              '& .MuiMenu-list': {
                padding: '0px !important',
              },
            }}
          >
            <MenuList
              sx={{
                boxShadow: 'unset !important',
                padding: '0px',
              }}
            >
              <Box width="180px">
                {DATA_MODE_THEME?.map((item, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => handleThemeChange(item.mode)}
                    selected={item.mode === themeMode}
                  >
                    <ListItem name={item.name} />
                  </MenuItem>
                ))}
              </Box>
            </MenuList>
          </Menu>
        )}

        {showLanguage && (
          <Menu
            open={showLanguage}
            anchorEl={anchorElLanguage}
            onClose={handleCloseUser}
            anchorOrigin={{
              vertical: dashboardPage ? 'bottom' : 'top',
              horizontal: dashboardPage ? 'right' : 'left',
            }}
            transformOrigin={{
              vertical: dashboardPage ? 'center' : 'top',
              horizontal: dashboardPage ? 'left' : 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: 'transparent',
                boxShadow: 'unset !important',
                padding: '0px',
              },
              '& .MuiMenu-list': {
                padding: '0px !important',
              },
            }}
          >
            <MenuList
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'unset !important',
                padding: '0px',
              }}
            >
              <Box sx={childMenuStyle}>
                <LanguageSwitcher
                  variant="minimal"
                  showFlag={true}
                  showText={true}
                  onLanguageChange={() => setShowLanguage(false)}
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    padding: '10px 16px',
                    borderRadius: 0,
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: 'text.main',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'background.overrideDefault',
                      color: 'primary.main',
                    },
                  }}
                />
              </Box>
            </MenuList>
          </Menu>
        )}

        {showGMT && (
          <Menu
            open={showGMT}
            anchorEl={anchorElGMT}
            onClose={handleCloseUser}
            anchorOrigin={{
              vertical: 'top',
              horizontal: dashboardPage ? 'right' : 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: dashboardPage ? 'left' : 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: 'transparent',
                boxShadow: 'unset !important',
                padding: '0px',
              },
              '& .MuiMenu-list': {
                padding: '0px !important',
              },
            }}
          >
            <MenuList
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'unset !important',
                padding: '0px',
              }}
            >
              <Box
                sx={{
                  ...childMenuStyle,
                  overflowY: 'scroll',
                  maxHeight: '341px',
                  width: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                    height: '20px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#D7D7D7',
                    borderRadius: '5px',
                    width: '6px',
                    height: '20px',
                  },
                }}
              >
                {timezoneOptions?.map((item: any) => (
                  <MenuItem
                    key={item.id}
                    onClick={() => handleClickGMT(item.id)}
                    selected={user?.gmtTimeZone?.id === item.id}
                    ref={user?.gmtTimeZone?.id === item.id ? gmtRef : null}
                  >
                    <ListItem name={item.displayName} showTimeShortUTC />
                  </MenuItem>
                ))}
              </Box>
            </MenuList>
          </Menu>
        )}

        {showAlert && (
          <Menu
            open={showAlert}
            anchorEl={anchorElAlert}
            onClose={handleCloseUser}
            anchorOrigin={{
              vertical: 'top',
              horizontal: dashboardPage ? 'right' : 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: dashboardPage ? 'left' : 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: 'transparent',
                boxShadow: 'unset !important',
                padding: '0px',
              },
            }}
          >
            <MenuList
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'unset !important',
                padding: '0px',
              }}
            >
              <Box
                sx={{
                  ...childMenuStyle,
                  overflowY: 'scroll',
                  height: '100%',
                  maxHeight: '583px',
                  width: '495px',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                    height: '20px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#D7D7D7',
                    borderRadius: '5px',
                    width: '6px',
                    height: '20px',
                  },
                }}
              >
                <ListSelectAlerts
                  handleCloseUser={handleCloseUser}
                  email={user?.email}
                />
              </Box>
            </MenuList>
          </Menu>
        )}
      </Grid>

      <Dialog open={openChangePasswordModal} onClose={handleCloseChangePassord}>
        <ChangePasswordView closeModal={handleCloseChangePassord} />
      </Dialog>
    </form>
  )
}

export default UserNav
